import { useRef, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { Redirect, useHistory } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/lib/auth/useAuth';
import { getAuthRedirectUrl } from '@/lib/auth/deepLinks';
import { Logo } from '@/components/ui/Logo';

// True when running as a home-screen PWA (no browser chrome) on any platform.
const isStandalone =
  typeof window !== 'undefined' &&
  (
    (window.navigator as { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );

function isRateLimitError(msg: string) {
  return /rate.?limit|too many request|for security purposes/i.test(msg);
}

type Step = 'form' | 'code';

export default function AuthPage() {
  const { status } = useAuth();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<Step>('form');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);

  // OTP code state — each digit in its own input for clean mobile UX
  const OTP_LENGTH = 6;
  const [code, setCode] = useState(Array(OTP_LENGTH).fill(''));
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  if (status === 'authenticated') return <Redirect to="/" />;
  if (status === 'needs-handle') return <Redirect to="/auth/claim-handle" />;

  function dismiss() {
    if (history.length > 1) history.goBack();
    else history.replace('/');
  }

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: getAuthRedirectUrl(),
        shouldCreateUser: true,
      },
    });
    setLoading(false);
    if (err) {
      if (isRateLimitError(err.message)) {
        setRateLimited(true);
        setError('Too many sign-in attempts. Please wait a few minutes before trying again.');
      } else {
        setError(err.message);
      }
      return;
    }
    setRateLimited(false);
    setCode(Array(OTP_LENGTH).fill(''));
    setStep('code');
    setTimeout(() => codeRefs.current[0]?.focus(), 80);
  }

  function clearCode() {
    setCode(Array(OTP_LENGTH).fill(''));
    setTimeout(() => codeRefs.current[0]?.focus(), 40);
  }

  async function resendCode() {
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: getAuthRedirectUrl(), shouldCreateUser: true },
    });
    setLoading(false);
    if (err) {
      if (isRateLimitError(err.message)) {
        setRateLimited(true);
        setError('Too many sign-in attempts. Please wait a few minutes, then tap Resend again.');
      } else {
        setError(err.message);
      }
      return;
    }
    setRateLimited(false);
    clearCode();
  }

  async function verifyCode(fullCode: string) {
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: fullCode,
      type: 'email',
    });
    setLoading(false);
    if (err) {
      if (isRateLimitError(err.message)) {
        setRateLimited(true);
        setError('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        setError(err.message);
      }
      clearCode();
    }
    // On success, onAuthStateChange in AuthProvider updates status → Redirect fires above.
  }

  function handleDigit(index: number, value: string) {
    // For paste: find the first run of exactly OTP_LENGTH consecutive digits in the pasted text.
    // This handles Outlook/email clients that wrap the code in surrounding sentences.
    const otpDigits = value.match(new RegExp(`\\d{${OTP_LENGTH}}`));
    const cleaned = otpDigits ? otpDigits[0] : value.replace(/\D/g, '');

    if (cleaned.length > 1) {
      const digits = cleaned.slice(0, OTP_LENGTH).split('');
      const next = [...code];
      digits.forEach((d, i) => { if (index + i < OTP_LENGTH) next[index + i] = d; });
      setCode(next);
      const focusIdx = Math.min(index + digits.length, OTP_LENGTH - 1);
      codeRefs.current[focusIdx]?.focus();
      const full = next.join('');
      if (full.length === OTP_LENGTH) verifyCode(full);
      return;
    }
    const next = [...code];
    next[index] = cleaned.slice(-1);
    setCode(next);
    if (cleaned && index < OTP_LENGTH - 1) codeRefs.current[index + 1]?.focus();
    const full = next.join('');
    if (full.length === OTP_LENGTH) verifyCode(full);
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  }

  async function signInWithGoogle() {
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: getAuthRedirectUrl(), skipBrowserRedirect: false },
    });
    if (err) {
      if (err.message.includes('provider is not enabled') || err.message.includes('Unsupported provider')) {
        setError('Google sign-in is not configured yet. Use email sign-in above.');
      } else {
        setError(err.message);
      }
    }
  }

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="relative flex min-h-full flex-col items-center justify-center bg-[#faf9f7] px-6 py-16">

          {/* Close / continue reading button */}
          <button
            onClick={dismiss}
            aria-label="Continue reading without signing in"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>

          <div className="w-full max-w-sm">
            <div className="mb-8 flex justify-center">
              <Logo size="lg" />
            </div>

            {step === 'form' ? (
              <>
                <h1 className="mb-2 text-center font-serif text-2xl font-bold text-gray-900">
                  Sign in to Open iPub
                </h1>
                <p className="mb-8 text-center text-sm text-gray-500">
                  We'll send a 6-digit sign-in code to your email.
                </p>

                <form onSubmit={sendCode} className="flex flex-col gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    disabled={rateLimited}
                    className="w-full rounded-lg border border-gray-200 px-4 py-3 text-base outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0] disabled:opacity-50"
                  />

                  {error && (
                    <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !email.trim() || rateLimited}
                    className="rounded-lg bg-[#2f3e46] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
                  >
                    {loading ? 'Sending…' : 'Send code'}
                  </button>
                </form>

                <div className="my-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-xs text-gray-400">or</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>

                <button
                  onClick={signInWithGoogle}
                  className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>

                <p className="mt-6 text-center text-xs text-gray-400">
                  By signing in you agree to our terms. Open iPub is a platform for reading and annotation.
                </p>
              </>
            ) : (
              /* ── Code entry step ── */
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1a56a0" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                </div>

                <h2 className="mb-1 font-serif text-xl font-bold text-gray-900">Check your email</h2>
                <p className="mb-1 text-sm text-gray-500">We sent a sign-in email to</p>
                <p className="mb-3 text-sm font-semibold text-gray-900">{email}</p>

                {!isStandalone && (
                  <p className="mb-5 text-sm text-gray-500">
                    <strong>Click the link</strong> in the email to sign in instantly,
                    or enter the 6-digit code below.
                  </p>
                )}
                {isStandalone && (
                  <p className="mb-5 text-sm text-gray-500">
                    Enter the 6-digit code from the email below.
                  </p>
                )}

                {/* 6-digit code inputs */}
                <div className="mb-4 flex justify-center gap-2">
                  {code.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => { codeRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={OTP_LENGTH}
                      value={digit}
                      onChange={e => handleDigit(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      onFocus={e => e.target.select()}
                      disabled={loading || rateLimited}
                      className="h-12 w-10 rounded-lg border border-gray-200 text-center text-base font-semibold outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0] disabled:opacity-50"
                      aria-label={`Digit ${i + 1}`}
                    />
                  ))}
                </div>

                {error && (
                  <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                )}

                {loading && (
                  <div className="mb-3 flex justify-center">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
                  </div>
                )}

                <div className="flex flex-col items-center gap-2">
                  {!rateLimited && (
                    <button
                      onClick={resendCode}
                      disabled={loading}
                      className="text-sm text-[#1a56a0] hover:underline disabled:opacity-50"
                    >
                      Resend code
                    </button>
                  )}
                  <button
                    onClick={() => { setStep('form'); setError(null); setCode(['', '', '', '', '', '']); setRateLimited(false); }}
                    className="text-sm text-gray-400 hover:underline"
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
